import React from "react";
import "./EvaluationSettings.less";
import strings from "./EvaluationSettings.strings";
import {
    GuxToggle,
    GuxFormFieldNumber,
    GuxFormFieldRadio,
} from "genesys-spark-components-react";
import { toGuxDisabled } from "util/componentUtil";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useStrings } from "features/i18n/useStrings";
import {
    getEvaluationSettings,
    qualityFormActions,
    selectCanEditForm,
} from "features/qualityForm/qualityFormSlice";
import { invalidIntegerInputCharacters } from "features/qualityForm/qualityFormValidations";
import { DefaultLimits } from "constants/DefaultLimits";
import SearchDropdown, { SearchResult } from "./SearchDropdown";
import {
    fetchUsers,
    selectListedUsers,
    usersSliceActions,
} from "features/users/usersSlice";
import { useState, InputEvent } from "react";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";
import { EvaluationSettingsAssigneeTypeEnum } from "@genesys/cloud-frontend/dist/mjs/models";

function DisputeAssigneeOption({
    index,
    value,
    checked,
    disabled,
    label,
    help,
}: {
    index: number;
    label: string;
    value: EvaluationSettingsAssigneeTypeEnum;
    checked: boolean;
    disabled: boolean;
    help: string;
}) {
    const dispatch = useAppDispatch();

    return (
        <GuxFormFieldRadio className="DisputeRadioButton">
            <input
                slot="input"
                type="radio"
                name={`dispute-assignment-${index}`}
                value={value}
                disabled={disabled}
                checked={checked}
                onChange={({ currentTarget }) =>
                    dispatch(
                        qualityFormActions.setAssigneeType({
                            index,
                            type: currentTarget.value as EvaluationSettingsAssigneeTypeEnum,
                        })
                    )
                }
            />
            <label slot="label">{label}</label>
            <span slot="help">{help}</span>
        </GuxFormFieldRadio>
    );
}

function DisputeAssignmentOptions() {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const disputeStrings = [
        t.labels.FirstDispute,
        t.labels.SecondDispute,
        t.labels.ThirdDispute,
        t.labels.FourthDispute,
        t.labels.FifthDispute,
    ];
    const disputeAssigneeOptions = [
        {
            value: EvaluationSettingsAssigneeTypeEnum.Original,
            label: t.labels.PreviousEvaluatorLabel,
            help: t.labels.PreviousEvaluatorHelp,
        },
        {
            value: EvaluationSettingsAssigneeTypeEnum.Individual,
            label: t.labels.DisputeAssignIndividualLabel,
            help: t.labels.DisputeAssignIndividualHelp,
        },
        {
            value: EvaluationSettingsAssigneeTypeEnum.None,
            label: t.labels.DisputeAssignNoneLabel,
            help: t.labels.DisputeAssignNoneHelp,
        },
    ];

    function DisputeString(index: number): string {
        if (index < disputeStrings.length) {
            return disputeStrings[index];
        }
        return t.labels.NDispute({ disputeNumber: index + 1 });
    }

    function onSelectedUser(index: number, assigneeUser: SearchResult) {
        dispatch(qualityFormActions.setAssigneeUser({ index, assigneeUser }));
    }

    const { disputesAllowedPerEvaluation, disputeAssignments } = useAppSelector(
        getEvaluationSettings
    );

    const disableEvaluationSettings = !useAppSelector(selectCanEditForm);

    const listedUsers = useAppSelector(selectListedUsers);

    const [searchIndex, setSearchIndex] = useState<number>(0);

    return (
        <>
            {disputeAssignments
                .slice(0, disputesAllowedPerEvaluation)
                .map((assignment, index) => (
                    <div className="DisputeCard dispute-card-flare" key={index}>
                        <div className="gux-heading-xs-semibold">
                            {DisputeString(index)}
                        </div>
                        <div className="separator" />
                        <div className="heading">{t.labels.NextAssignee}</div>
                        {disputeAssigneeOptions.map((option) => (
                            <React.Fragment key={option.value}>
                                <DisputeAssigneeOption
                                    key={option.value}
                                    disabled={disableEvaluationSettings}
                                    index={index}
                                    value={option.value}
                                    checked={assignment.type === option.value}
                                    label={option.label}
                                    help={option.help}
                                />
                                {option.value ===
                                    EvaluationSettingsAssigneeTypeEnum.Individual &&
                                    assignment.type ===
                                        EvaluationSettingsAssigneeTypeEnum.Individual && (
                                        <>
                                            <div className="UserDropdown">
                                                <SearchDropdown
                                                    disabled={
                                                        disableEvaluationSettings
                                                    }
                                                    onSearch={async (input) => {
                                                        dispatch(
                                                            fetchUsers(input)
                                                        );
                                                        setSearchIndex(index);
                                                    }}
                                                    onExpanded={async (
                                                        input
                                                    ) => {
                                                        dispatch(
                                                            fetchUsers(input)
                                                        );
                                                        setSearchIndex(index);
                                                    }}
                                                    onCollapsed={async () => {
                                                        dispatch(
                                                            usersSliceActions.clearUsers()
                                                        );
                                                    }}
                                                    onSelection={async (
                                                        input
                                                    ) => {
                                                        if (
                                                            assignment.assigneeUserError &&
                                                            (!assignment
                                                                .assigneeUser
                                                                ?.value ||
                                                                assignment
                                                                    .assigneeUser
                                                                    .value !==
                                                                    input.value)
                                                        ) {
                                                            dispatch(
                                                                qualityFormActions.setAssigneeUserError(
                                                                    {
                                                                        index,
                                                                        error: false,
                                                                    }
                                                                )
                                                            );
                                                        }
                                                        onSelectedUser(
                                                            index,
                                                            input
                                                        );
                                                    }}
                                                    selected={
                                                        assignment.assigneeUser
                                                    }
                                                    hasError={
                                                        assignment.assigneeUserError
                                                    }
                                                    results={
                                                        searchIndex === index
                                                            ? Object.values(
                                                                  listedUsers
                                                              ).map((user) => ({
                                                                  name:
                                                                      user.name ||
                                                                      "",
                                                                  value:
                                                                      user.id ||
                                                                      "",
                                                                  version: 0,
                                                              })) || []
                                                            : []
                                                    }
                                                    placeholder={
                                                        t.labels
                                                            .IndividualAssigneeDefault
                                                    }
                                                    ariaLabel={
                                                        t.labels
                                                            .SearchDropdownDescription
                                                    }
                                                />
                                            </div>
                                            {assignment.assigneeUserError && (
                                                <ErrorStatement
                                                    errorMessage={
                                                        assignment.assigneeUser ==
                                                        null
                                                            ? t.labels
                                                                  .IndividualNotSelected
                                                            : t.labels
                                                                  .IndividualNotValid
                                                    }
                                                />
                                            )}
                                        </>
                                    )}
                            </React.Fragment>
                        ))}
                    </div>
                ))}
        </>
    );
}

function DisputePerEvaluation() {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const { disputesAllowedPerEvaluation } = useAppSelector(
        getEvaluationSettings
    );

    const disableEvaluationSettings = !useAppSelector(selectCanEditForm);

    return (
        <>
            <div className="heading">{t.labels.DisputesPerEvaluation}</div>
            <div className="DisputePerEvaluation">
                <GuxFormFieldNumber labelPosition="screenreader">
                    <input
                        slot="input"
                        name="number"
                        type="number"
                        step="1"
                        disabled={disableEvaluationSettings}
                        min={DefaultLimits.minDisputesPerEvaluation}
                        max={DefaultLimits.maxDisputesPerEvaluation}
                        value={disputesAllowedPerEvaluation}
                        onBeforeInput={(
                            event: InputEvent<HTMLInputElement>
                        ) => {
                            if (
                                invalidIntegerInputCharacters.includes(
                                    event.data
                                )
                            ) {
                                event.preventDefault();
                            }
                        }}
                        onChange={({ currentTarget }) => {
                            dispatch(
                                qualityFormActions.setDisputesAllowedPerEvaluation(
                                    {
                                        disputesAllowedPerEvaluation:
                                            currentTarget.valueAsNumber,
                                    }
                                )
                            );
                        }}
                    />
                    <label slot="label">{t.labels.DisputesPerEvaluation}</label>
                </GuxFormFieldNumber>
            </div>
        </>
    );
}

function DetailedToggle({
    heading,
    description,
    checked,
    onCheck,
    disabled,
}: {
    heading: string;
    description: string;
    checked: boolean;
    onCheck: () => void;
    disabled?: boolean;
}) {
    const t = useStrings(strings);

    return (
        <>
            <div className="heading">{heading}</div>
            {/* TODO: there will be no checked & uncheckedLabel in the future release */}
            <GuxToggle
                checkedLabel={t.labels.Enabled}
                uncheckedLabel={t.labels.Disabled}
                checked={checked}
                onCheck={onCheck}
                disabled={toGuxDisabled(disabled)}
                aria-disabled={toGuxDisabled(disabled)}
            />
            <p>{description}</p>
        </>
    );
}

export function EvaluationSettings() {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const { revisionsEnabled, disputesEnabled } = useAppSelector(
        getEvaluationSettings
    );

    const disableEvaluationSettings = !useAppSelector(selectCanEditForm);
    return (
        <div className="EvaluationSettingsPanel">
            <div className="RevisionsSettings">
                <h3>{t.labels.Revisions}</h3>
                <div className="separator" />
                <DetailedToggle
                    heading={t.labels.AllowRevisions}
                    description={t.labels.RevisionsDescription}
                    checked={revisionsEnabled}
                    onCheck={() => {
                        dispatch(qualityFormActions.toggleRevisions());
                    }}
                    disabled={disableEvaluationSettings}
                />
            </div>
            <div className="DisputesSettings">
                <h3>{t.labels.Disputes}</h3>
                <div className="separator" />
                <DetailedToggle
                    heading={t.labels.AllowDisputes}
                    description={t.labels.DisputesDescription}
                    checked={disputesEnabled}
                    onCheck={() => {
                        dispatch(qualityFormActions.toggleDisputes());
                    }}
                    disabled={disableEvaluationSettings}
                />
                {disputesEnabled && (
                    <div className="DisputeToggleSettings">
                        <DisputePerEvaluation />
                        <DisputeAssignmentOptions />
                    </div>
                )}
            </div>
        </div>
    );
}
